import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '../../core/components/Alert.tsx';
import Button from '../../core/components/Button.tsx';
import Checkbox from '../../core/components/Checkbox.tsx';
import TextField from '../../core/components/Fields/TextField.tsx';
import FormField from '../../core/components/FormField.tsx';
import Modal from '../../core/components/Modal.tsx';
import { useToaster } from '../../core/components/Toast.tsx';
import { graphqlErrorMessage } from '../errors.ts';
import { GET_CONVERSATION, GET_CONVERSATIONS, SHARE_CONVERSATION } from '../queries.ts';
import { RecipientsField, recipientsLabel, splitRecipients } from './RecipientsField.tsx';

/** "Team Sales, Anna Schmidt" — who a share went to. */
export const shareRecipientsLabel = (share, t) => recipientsLabel(share.team, share.members, t);

/** The user's question a reply answers: the closest user message before it. */
const questionOf = (messages, reply) => {
    const index = messages.findIndex((candidate) => candidate.id === reply.id);
    for (let i = index; i >= 0; i -= 1) {
        if (messages[i].role === 'user') return messages[i].content;
    }
    return '';
};

/** Hands a chat — or one exchange of it — to a team and/or named people as a frozen snapshot (ENG-11386). Opened
 *  from the thread header for the whole chat, with "only the last task" as the alternative, or from under a reply for
 *  that exchange, with "the whole conversation instead" as the alternative. Recipients follow the project rule
 *  (one of your teams and/or people from them); who already sees the chat is managed in the Access popover. */
export const ShareConversationModal = ({
    conversation,
    message = null,
    onClose,
}: {
    conversation: any;
    message?: any | null;
    onClose: () => void;
}) => {
    const { t } = useTranslation('chat');
    const { publishToast } = useToaster();
    const [alternative, setAlternative] = useState(false);
    const [recipients, setRecipients] = useState<string[]>([]);
    const [note, setNote] = useState('');

    const [shareConversation, { loading: sharing }] = useMutation(SHARE_CONVERSATION, {
        refetchQueries: [{ query: GET_CONVERSATION, variables: { id: conversation.id } }, { query: GET_CONVERSATIONS }],
    });
    const { teamId, memberIds } = splitRecipients(recipients);

    const messages = conversation.messages || [];
    const replies = messages.filter((candidate) => candidate.role === 'assistant');
    const lastReply = replies[replies.length - 1] || null;
    // from a reply: that exchange unless "the whole conversation instead"; from the header: everything unless
    // "only the last task"
    const taskMessage = message ? (alternative ? null : message) : alternative ? lastReply : null;
    const canShare = recipients.length > 0;

    const handleShare = async () => {
        try {
            const { data } = await shareConversation({
                variables: {
                    id: conversation.id,
                    teamId,
                    memberIds,
                    messageId: taskMessage?.id ?? null,
                    note: note.trim() || null,
                },
            });
            publishToast({
                description: t('share.done', {
                    recipients: shareRecipientsLabel(data.shareAgentConversation.share, t),
                }),
                status: 'success',
            });
            onClose();
        } catch (e) {
            publishToast({ description: graphqlErrorMessage(e, t('share.error')), status: 'error' });
        }
    };

    const buttons = (
        <>
            <Button variant="outline" onClick={onClose} disabled={sharing}>
                {t('share.cancel')}
            </Button>
            <Button variant="primary" onClick={handleShare} disabled={!canShare || sharing} loading={sharing}>
                {t('share.submit')}
            </Button>
        </>
    );

    return (
        <Modal title={t('share.title')} onClose={onClose} buttons={buttons} className="w-full sm:w-144">
            <div className="flex flex-col gap-4">
                {taskMessage && (
                    <div className="rounded-lg bg-surface-medium px-3 py-2 text-sm text-content-medium">
                        {t('share.taskQuote', { question: questionOf(messages, taskMessage) })}
                    </div>
                )}
                {(message || lastReply) && (
                    <label className="flex cursor-pointer items-center gap-2 text-sm text-content-strong">
                        <Checkbox checked={alternative} onChange={(e) => setAlternative(e.target.checked)} />
                        {message ? t('share.wholeInstead') : t('share.onlyLastTask')}
                    </label>
                )}

                <FormField
                    label={t('share.recipients')}
                    helpText={recipients.length === 0 ? t('share.noneSelected') : t('share.canViewHint')}
                >
                    <RecipientsField autoFocus value={recipients} onValueChange={setRecipients} />
                </FormField>
                <FormField label={t('share.note')} helpText={t('share.noteHelp')}>
                    <TextField value={note} onValueChange={setNote} placeholder={t('share.notePlaceholder')} rows={3} />
                </FormField>

                <Alert severity="info">{t('share.info')}</Alert>
            </div>
        </Modal>
    );
};
